import React from 'react'
import PropTypes from 'prop-types'
import { useIntl, FormattedMessage } from 'react-intl'

// Components
import {
    Container,
    SimpleGrid,
    Box,
    Text,

    // Hooks
    useTheme
} from '@salesforce/retail-react-app/app/components/shared/ui'

// Project Components
import LinksList from '../links-list'

// Others
import {renderComponentsSwitch} from '../../components/optimizely/utils/contentRenderer'
import {transformMegaMenuBlockToCategories} from '../../components/optimizely/utils/utils'

/**
 * Helper to build category URL.
 * The item should already have c_relativeUrl populated from the API response.
 * Falls back to categoryUrlBuilder with categories map as secondary lookup.
 * @param {Object} item - The category item (should have c_relativeUrl from API)
 * @param {Object} categories - Map of categoryId -> full category data (fallback)
 * @returns {string} The category URL
 */
const getCategoryUrl = (item, categories) => {
    // The item itself should have c_relativeUrl from the API response
    if (item?.c_relativeUrl) {
        return encodeURI(item.c_relativeUrl)
    }

    // Try to get from categories map (in case item is partial)
    const fullCategory = categories?.[item?.id]
    if (fullCategory?.c_relativeUrl) {
        return encodeURI(fullCategory.c_relativeUrl)
    }

    // No URL available
    return null
}

/**
 * Renders CMS-driven mega menu content when MegaMenuItemLevelOneBlock is present
 */
const CMSMegaMenuContent = ({ item, onClose, baseStyle, categories }) => {
    const cmsColumns = transformMegaMenuBlockToCategories(item.CMSMegaMenuData)
    const maxColumnSize = '217px'

    // Illustration blocks are siblings to columns at the MegaMenuItemLevelOneBlock level
    const illustrationBlocks = cmsColumns.illustrationBlocksFromParent || []
    const hasIllustrations = illustrationBlocks.length > 0

    return (
        <Container
            {...baseStyle.popoverContainer}
            {...(hasIllustrations && baseStyle.popoverContainerWithImages)}
        >
            <SimpleGrid
                spacing={4}
                gridTemplateColumns={`repeat(${cmsColumns.length}, minmax(0, ${maxColumnSize}))`}
            >
                {cmsColumns.map((column, columnIndex) => {
                    // Get column header: prefer CMS ColumnTitle, then API category name
                    const columnCategoryFromAPI = categories?.[column.id]
                    const columnName = column.name || columnCategoryFromAPI?.name

                    // Build links for this column
                    const columnLinks = column.categories?.map((categoryItem) => {
                        const cmsData = categoryItem.cmsCategoryData
                        // Try to get c_relativeUrl from API categories
                        const apiCategory = categories?.[categoryItem.id]

                        return {
                            href: getCategoryUrl(
                                apiCategory || { id: categoryItem.id },
                                categories
                            ),
                            text: cmsData?.OverrideName || apiCategory?.name || categoryItem.name,
                            cmsCategoryReplacement: cmsData
                        }
                    }) || []

                    return (
                        <Box key={`cms-column-${column.id || columnIndex}`}>
                            <Text {...baseStyle.level1Label}>
                                {columnName}
                            </Text>
                            <LinksList
                                variant={'menuItem'}
                                key={`cms-links-${column.id || columnIndex}`}
                                links={columnLinks}
                                onLinkClick={onClose}
                            />
                        </Box>
                    )
                })}
            </SimpleGrid>

            {hasIllustrations && (
                <Box {...baseStyle.imageBlockContainer}>
                    {illustrationBlocks.map((illustrationBlock, index) => {
                        const expandedBlock = illustrationBlock?.ContentLink?.Expanded || illustrationBlock?.Expanded
                        return renderComponentsSwitch(expandedBlock, {index, onClose})
                    })}
                </Box>
            )}
        </Container>
    )
}

CMSMegaMenuContent.propTypes = {
    item: PropTypes.object,
    onClose: PropTypes.func,
    baseStyle: PropTypes.object,
    categories: PropTypes.object
}

/**
 * Renders CMS illustration-only mega menu content (no category columns)
 * Used when MegaMenuItemLevelOneBlock has only illustrations configured
 */
const CMSIllustrationOnlyContent = ({ item, baseStyle, onClose }) => {
    const illustrationBlocks = item?.CMSMegaMenuData?.MegaMenuIlustrationBlockList || []

    return (
        <Container
            {...baseStyle.popoverContainer}
            {...baseStyle.popoverContainerWithImages}
        >
            <Box {...baseStyle.imageBlockContainer}>
                {illustrationBlocks.map((illustrationBlock, index) => {
                    const expandedBlock = illustrationBlock?.ContentLink?.Expanded || illustrationBlock?.Expanded
                    return renderComponentsSwitch(expandedBlock, {index, onClose})
                })}
            </Box>
        </Container>
    )
}

CMSIllustrationOnlyContent.propTypes = {
    item: PropTypes.object,
    baseStyle: PropTypes.object,
    onClose: PropTypes.func
}

/**
 * Default API-driven mega menu content
 */
const DefaultMegaMenuContent = ({ item, itemsKey, onClose, baseStyle, categories }) => {
    const intl = useIntl()
    const items = item?.[itemsKey] || []

    const menuColumns = items.filter((item) => item.c_showOnSeparateColumn).length + 1
    const maxColumnSize = '217px'
    const l1CategoryId = items[0]?.parentCategoryId
    const l1CategoryName = items[0]?.parentCategoryTree?.find(
        (item) => item.id === l1CategoryId
    )?.name

    // Get the complete L1 category object from categories (which includes c_relativeUrl)
    const l1CategoryObject = categories && l1CategoryId ? categories[l1CategoryId] : null

    const allXL1 = l1CategoryObject
        ? {
            ...l1CategoryObject,
            name: l1CategoryName && intl.formatMessage(
                {
                    id: 'nav_alXL1.label',
                    defaultMessage: 'All {l1CategoryName}'
                },
                { l1CategoryName }
            )
        }
        : {
            id: l1CategoryId,
            name:l1CategoryName && intl.formatMessage(
                {
                    id: 'nav_alXL1.label',
                    defaultMessage: 'All {l1CategoryName}'
                },
                { l1CategoryName }
            )
        }

    const allXL1CategoryReplacement = allXL1?.CategoryReplacement?.ContentLink?.Expanded

    const l1Heading = l1CategoryObject?.c_showAllOnL2 ? {
        cmsCategoryReplacement: allXL1CategoryReplacement,
        href: getCategoryUrl(allXL1, categories),
        text: allXL1.name,
        wrapperStyles: {
            lineHeight: '12px',
        }
    } : ''

    const l1links = items.map((item) => {
        const { name } = item
        const cmsCategoryReplacement = item?.CategoryReplacement?.ContentLink?.Expanded
        return {
            href: getCategoryUrl(item, categories),
            text: name,
            cmsCategoryReplacement: cmsCategoryReplacement
        }
    })

    const MegaMenuIlustrationBlockList = item?.MegaMenuIlustrationBlockList || item?.CategoryReplacement?.ContentLink?.Expanded?.MenuCategoryIllustrationsBlock?.Expanded?.Illustrations

    return (
        <Container {...baseStyle.popoverContainer}
            {...(MegaMenuIlustrationBlockList && baseStyle.popoverContainerWithImages)}
            >
            <SimpleGrid
                spacing={4} // 16px gap
                gridTemplateColumns={`repeat(${menuColumns}, minmax(0, ${maxColumnSize})) 1fr`}
            >
                <Box>
                    <Text {...baseStyle.level1Label}>
                        <FormattedMessage id="nav_level1.label" defaultMessage="Category" />
                    </Text>

                    <LinksList
                        variant={'menuItem'}
                        key={'l1-links'}
                        heading={l1Heading}
                        links={l1links}
                        onLinkClick={onClose}
                    />
                </Box>

                {items
                    .filter((item) => item.c_showOnSeparateColumn)
                    .map((item) => {
                        const { id, name: l2CategoryName, c_showSeparator: l2Separator } = item
                        const l2Items = item[itemsKey]
                        const cmsCategoryReplacement = item?.CategoryReplacement?.ContentLink?.Expanded
                        const l2Heading = item?.c_showAllOnL2
                            ? {
                                href: getCategoryUrl(item, categories),
                                text: intl.formatMessage(
                                    {
                                        id: 'nav_alXL2.label',
                                        defaultMessage: 'All {l2CategoryName}'
                                    },
                                    { l2CategoryName }
                                ),
                                wrapperStyles: l2Separator && { ...baseStyle.linkSeparator },
                                cmsCategoryReplacement: cmsCategoryReplacement
                            }
                            : ''

                        const l2links = l2Items?.map((item) => {
                            const { name: l3CategoryName, c_showSeparator: l3Separator } = item
                            const cmsCategoryReplacement = item?.CategoryReplacement?.ContentLink?.Expanded

                            return {
                                href: getCategoryUrl(item, categories),
                                text: l3CategoryName,
                                defaultListStyle: l3Separator && { ...baseStyle.linkSeparator },
                                cmsCategoryReplacement: cmsCategoryReplacement
                            }
                        })

                        return (
                            <Box key={`link-wrapper-${id}`}>
                                <Text key={`l2-label-${id}`} {...baseStyle.level1Label}>
                                    {l2CategoryName}
                                </Text>
                                <LinksList
                                    variant={'menuItem'}
                                    key={`l2-link-list-${id}`}
                                    heading={l2Heading}
                                    links={l2links}
                                    onLinkClick={onClose}
                                />
                            </Box>
                        )
                    })}
            </SimpleGrid>
            {(MegaMenuIlustrationBlockList || item?.MenuCategoryIllustrationsBlock?.Expanded ) &&
                <Box {...baseStyle.imageBlockContainer}>
                    {MegaMenuIlustrationBlockList?.map((MegaMenuIlustrationBlock) => {
                        return renderComponentsSwitch(
                            MegaMenuIlustrationBlock?.ContentLink?.Expanded || MegaMenuIlustrationBlock?.Expanded,
                            {onClose}
                        )
                    })}
                </Box>
            }
        </Container>
    )
}

DefaultMegaMenuContent.propTypes = {
    item: PropTypes.object,
    itemsKey: PropTypes.string,
    onClose: PropTypes.func,
    baseStyle: PropTypes.object,
    categories: PropTypes.object
}

/**
 * Main ListMenuContent component that switches between CMS and API-driven content
 */
const ListMenuContent = ({ maxColumns, item, itemsKey, onClose, initialFocusRef, categories }) => {
    const theme = useTheme()
    const { baseStyle } = theme.components.ListMenu

    // Check if this category has a CMS-driven mega menu structure
    const hasCMSMegaMenu = item?.hasCMSMegaMenu && item?.CMSMegaMenuData
    const hasCMSColumns = item?.CMSMegaMenuData?.MegaMenuItemsLevelOne?.length > 0
    const hasCMSIllustrationsOnly = hasCMSMegaMenu &&
        !hasCMSColumns &&
        item?.CMSMegaMenuData?.MegaMenuIlustrationBlockList?.length > 0

    // Case 1: CMS mega menu with columns
    if (hasCMSMegaMenu && hasCMSColumns) {
        return (
            <CMSMegaMenuContent
                item={item}
                onClose={onClose}
                baseStyle={baseStyle}
                categories={categories}
            />
        )
    }

    // Case 2: CMS mega menu with only illustrations (no columns) - show only illustrations
    if (hasCMSIllustrationsOnly) {
        return (
            <CMSIllustrationOnlyContent
                item={item}
                baseStyle={baseStyle}
                onClose={onClose}
            />
        )
    }

    // Case 3: Default API-driven mega menu (no CMS mega menu configured)
    return (
        <DefaultMegaMenuContent
            item={item}
            itemsKey={itemsKey}
            onClose={onClose}
            baseStyle={baseStyle}
            categories={categories}
        />
    )
}

ListMenuContent.propTypes = {
    item: PropTypes.object,
    itemsKey: PropTypes.string,
    maxColumns: PropTypes.number,
    onClose: PropTypes.func,
    initialFocusRef: PropTypes.object,
    categories: PropTypes.object
}

export { ListMenuContent }
